import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useVehicles } from "../../hooks";

/**
 * mapsHref — Builds a Google Maps link for a vehicle's location from its
 * address string (SPR-010 — coordinates removed; maps use the address).
 *
 * @param {{ currentAddress?: string }} vehicle
 * @returns {string|null} A maps URL, or null when no address is known.
 */
function mapsHref(vehicle) {
  if (vehicle?.currentAddress) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      vehicle.currentAddress
    )}`;
  }
  return null;
}

/**
 * VehicleStatusCard — "Find the Bullet" treatment for a single vehicle (F-012).
 *
 * Renders an open/closed status pill, the current address, and a map link
 * when the vehicle is Active and parked somewhere. Styled per BLU-006:
 * white card, 2px charcoal border, hard offset shadow.
 *
 * @param {{ vehicle: object }} props
 */
function VehicleStatusCard({ vehicle }) {
  const isOpen = vehicle.status === "Active";
  const href = mapsHref(vehicle);

  return (
    <article
      className="bg-white border-2 border-charcoal rounded-2xl p-6 shadow-[4px_4px_0_rgba(43,39,36,0.12)]"
      data-testid={`vehicle-card-${vehicle.id}`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {vehicle.name ? (
          <h3 className="font-display text-2xl text-charcoal uppercase">{vehicle.name}</h3>
        ) : null}
        <span
          className={[
            "font-heading tracking-widest text-sm px-3 py-1 rounded-full",
            isOpen ? "bg-teal text-white" : "bg-mustard text-charcoal",
          ].join(" ")}
          data-testid={`vehicle-status-${vehicle.id}`}
        >
          {isOpen ? "ON THE ROAD" : "OFF DUTY"}
        </span>
      </div>

      {isOpen && vehicle.currentAddress ? (
        <p className="font-body text-ink/80 mt-3" data-testid={`vehicle-address-${vehicle.id}`}>
          Find us at <span className="font-semibold">{vehicle.currentAddress}</span>
        </p>
      ) : (
        <p className="font-body text-ink/60 italic mt-3" data-testid={`vehicle-next-${vehicle.id}`}>
          {isOpen
            ? "Rolling now — check back for our next stop."
            : "Sorry we missed you!"}
        </p>
      )}

      {/* "Get directions" — performs the map action (formerly the MAP badge).
          The separate MAP badge has been removed (SPR-009 P2-7). */}
      {isOpen && href && (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block mt-4 bg-rust text-white font-heading tracking-widest px-5 py-2 rounded-xl shadow-[0_4px_0_#7f371e] hover:translate-y-0.5 hover:shadow-[0_2px_0_#7f371e] transition-all"
          data-testid={`vehicle-map-link-${vehicle.id}`}
        >
          GET DIRECTIONS
        </a>
      )}
    </article>
  );
}

/**
 * VehicleCarousel — Swipeable carousel of vehicle status cards (F-012, P2-7).
 *
 * One card visible at a time; swipe (touch) or use the prev/next controls and
 * dots to move between vehicles when there's more than one. Falls back to a
 * single static card when only one vehicle is present.
 *
 * @param {{ vehicles: object[] }} props
 */
function VehicleCarousel({ vehicles }) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(null);
  const count = vehicles.length;

  // Keep the active index in range if the vehicle list shrinks.
  useEffect(() => {
    if (index > count - 1) setIndex(Math.max(0, count - 1));
  }, [count, index]);

  const go = (next) => {
    setIndex((i) => {
      const n = (next + count) % count;
      return n;
    });
  };

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 40) go(delta < 0 ? index + 1 : index - 1);
    touchStartX.current = null;
  };

  if (count === 1) {
    return <VehicleStatusCard vehicle={vehicles[0]} />;
  }

  return (
    <div data-testid="vehicle-carousel">
      <div className="overflow-hidden" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <div
          className="flex transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${index * 100}%)` }}
          data-testid="vehicle-carousel-track"
        >
          {vehicles.map((v) => (
            <div key={v.id} className="w-full shrink-0 px-0.5">
              <VehicleStatusCard vehicle={v} />
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between mt-4">
        <button
          type="button"
          onClick={() => go(index - 1)}
          aria-label="Previous vehicle"
          data-testid="vehicle-carousel-prev"
          className="font-heading tracking-widest text-sm text-teal border-2 border-teal rounded-xl px-4 py-1.5 hover:bg-teal hover:text-white transition-colors"
        >
          ‹ PREV
        </button>

        <div className="flex gap-2" role="tablist" aria-label="Vehicles">
          {vehicles.map((v, i) => (
            <button
              key={v.id}
              type="button"
              role="tab"
              onClick={() => setIndex(i)}
              aria-label={`Show vehicle ${i + 1}`}
              aria-selected={i === index}
              data-testid={`vehicle-carousel-dot-${i}`}
              className={[
                "h-2.5 w-2.5 rounded-full transition-colors",
                i === index ? "bg-rust" : "bg-aluminum-dark",
              ].join(" ")}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={() => go(index + 1)}
          aria-label="Next vehicle"
          data-testid="vehicle-carousel-next"
          className="font-heading tracking-widest text-sm text-teal border-2 border-teal rounded-xl px-4 py-1.5 hover:bg-teal hover:text-white transition-colors"
        >
          NEXT ›
        </button>
      </div>
    </div>
  );
}

/**
 * Home — Public landing page for Twin City Silver Bullet (BLU-006).
 *
 * Mirrors the live site (twincitysilverbullet.com): hero with the rig,
 * an "On the Road / Find the Bullet" live vehicle section (F-012), and a
 * "Why We Roll" mission teaser linking to the Give page.
 */
export default function Home() {
  const { vehicles, isLoading, error } = useVehicles();
  const activeFirst = [...vehicles].sort(
    (a, b) => (b.status === "Active") - (a.status === "Active")
  );

  return (
    <main
      data-testid="home-page"
      className="min-h-screen bg-cream text-ink font-body"
    >
      {/* Hero — The Rig */}
      <section className="flex flex-col items-center justify-center py-20 px-4 text-center">
        <p className="font-heading tracking-[0.3em] text-teal-deep text-sm mb-3">
          1967 AIRSTREAM · VETERAN-BUILT
        </p>
        <h1 className="font-display text-5xl md:text-7xl text-charcoal leading-tight uppercase">
          Twin City <span className="text-rust">Silver Bullet</span>
        </h1>
        <p className="font-body text-lg text-ink/70 mt-4 max-w-xl">
          A polished 1967 Airstream slinging coffee and scoops across the Twin Cities. Every cup
          helps build a bridge home.
        </p>
        <Link
          to="/menu"
          className="mt-8 inline-block bg-rust text-white font-heading text-xl tracking-widest px-8 py-3 rounded-xl shadow-[0_4px_0_#7f371e] hover:translate-y-0.5 hover:shadow-[0_2px_0_#7f371e] transition-all"
        >
          SEE THE MENU
        </Link>
      </section>

      {/* On the Road — Find the Bullet (F-012) */}
      <section
        className="max-w-3xl mx-auto px-4 py-12 border-t-2 border-line"
        aria-label="Find the Bullet"
        data-testid="find-the-bullet"
      >
        <h2 className="font-display text-3xl text-charcoal uppercase mb-1">On the Road</h2>
        <p className="font-body text-ink/60 mb-6">
          We don't have an address. We have a schedule. Chase the Silver Bullet.
        </p>

        {isLoading && (
          <p className="font-body text-ink/60 italic" data-testid="vehicles-loading">
            Locating the Bullet…
          </p>
        )}
        {error && (
          <p className="font-body text-rust" data-testid="vehicles-error">
            We couldn't load the Bullet's location right now. Try again soon.
          </p>
        )}
        {!isLoading && !error && vehicles.length === 0 && (
          <p className="font-body text-ink/60 italic" data-testid="vehicles-empty">
            No vehicles on the road yet — check back soon.
          </p>
        )}

        {!isLoading && !error && vehicles.length > 0 && (
          <VehicleCarousel vehicles={activeFirst} />
        )}
      </section>

      {/* Why We Roll — mission teaser */}
      <section
        className="max-w-3xl mx-auto px-4 py-12 border-t-2 border-line"
        aria-label="Why we roll"
      >
        <h2 className="font-display text-3xl text-charcoal uppercase mb-3">Why We Roll</h2>
        <p className="font-body text-ink/70 mb-5">
          Twin City Silver Bullet is run by Twin City Veterans Bridge, helping transitioning
          veterans build careers, ventures, and a place in this community.
        </p>
        <Link
          to="/give"
          className="font-heading tracking-widest text-teal border-b-2 border-teal hover:text-teal-deep transition-colors"
          data-testid="home-mission-link"
        >
          OUR MISSION →
        </Link>
      </section>
    </main>
  );
}
